// Decision logic for the video-clip `/cut` response, kept pure so it can be unit
// tested without a backend. The clip player uses these two helpers to turn a cut
// failure (S3 upload error surfaced as `errors`, or a 422 / missing path meaning
// the match has no video) into a concise toast + auto-skip instead of a blocking
// alert() or a silent black frame.

// Classify a parsed `/cut` response body.
// Returns { playable: true } when the response carries a usable video path,
// otherwise { playable: false, reason } with a short human-readable reason.
export function classifyCutResult(json) {
  if (!json || typeof json !== 'object') {
    return { playable: false, reason: 'no video returned' };
  }

  // S3 "BadDigest" and similar upload failures come back as an errors array.
  if (Array.isArray(json.errors) && json.errors.length) {
    return { playable: false, reason: json.errors.join(', ') };
  }

  // 422 / "no video for this match" cases leave data.path missing or the literal
  // string "null" — treat those as a failed clip rather than playing a black frame.
  const path = json.data && json.data.path;
  if (path == null || path === 'null' || path === '') {
    return { playable: false, reason: 'no video for this match' };
  }

  return { playable: true, reason: null };
}

// Find the next playable clip index after `fromIndex`, skipping braker headers and
// indices already known to have failed. Forward-only (no wrap-around) so it always
// terminates — this is the infinite-loop guard when every clip fails. Returns -1
// when no playable clip remains.
export function pickNextPlayableIndex(list, fromIndex, failedIndices) {
  if (!Array.isArray(list)) return -1;
  const failed = failedIndices instanceof Set ? failedIndices : new Set();
  for (let idx = fromIndex + 1; idx < list.length; idx += 1) {
    const item = list[idx];
    if (item && !item.braker && !failed.has(idx)) {
      return idx;
    }
  }
  return -1;
}
